/**
 * ETO Wine - Smile.io Member Detection Fix
 * This script prevents existing members from getting stuck in blank form loops
 * and provides helpful messaging when they try to sign up again.
 */

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    // How long to wait for Smile.io to fully load (milliseconds)
    smileLoadDelay: 1500,
    // Selectors for Smile.io forms AND Shopify register forms
    smileFormSelectors: [
      '[data-smile-signup-form]',
      '.smile-signup-form',
      '#smile-signup-form',
      'form[action*="smile"]',
      '.smile-ui-modal form',
      '#eto-register-form',  // ETO register page
      'form[action*="/account"]'  // Shopify account forms
    ],
    // Your store's branding colors
    colors: {
      background: '#f8f4f0',
      border: '#8B7355',
      text: '#2c2c2c',
      buttonBg: '#2c2c2c',
      buttonText: '#ffffff',
      linkColor: '#8B7355'
    }
  };

  /**
   * Find the Smile.io signup form on the page
   */
  function findSmileForm() {
    for (let selector of CONFIG.smileFormSelectors) {
      const form = document.querySelector(selector);
      if (form) {
        console.log('Found Smile form:', selector);
        return form;
      }
    }
    return null;
  }

  /**
   * Check if a customer account exists with the given email
   */
  async function checkIfCustomerExists(email) {
    try {
      // We'll attempt a login with an intentionally wrong password
      // If the email exists, we'll get a "wrong password" error
      // If the email doesn't exist, we'll get a "no account" error
      
      const formData = new URLSearchParams();
      formData.append('form_type', 'customer_login');
      formData.append('utf8', '✓');
      formData.append('customer[email]', email);
      formData.append('customer[password]', 'INTENTIONAL_WRONG_PASSWORD_CHECK_12345');

      const response = await fetch('/account/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
        redirect: 'manual' // Don't follow redirects
      });

      const html = await response.text();
      
      // Check the error message to determine if account exists
      const accountExists = html.includes('Incorrect email or password') || 
                           html.includes('Invalid login credentials') ||
                           html.includes('password is incorrect');
      
      return accountExists;
    } catch (error) {
      console.error('Error checking customer existence:', error);
      return false; // Assume doesn't exist if check fails
    }
  }

  /**
   * Display a friendly message when existing member tries to sign up
   */
  function showExistingMemberMessage(email, form) {
    const messageHTML = `
      <div class="eto-existing-member-notice" style="
        background: ${CONFIG.colors.background};
        border: 2px solid ${CONFIG.colors.border};
        border-radius: 8px;
        padding: 30px;
        margin: 20px 0;
        text-align: center;
        animation: slideIn 0.3s ease-out;
      ">
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="${CONFIG.colors.border}" stroke-width="2" style="margin: 0 auto 15px;">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        
        <h3 style="
          margin: 0 0 10px 0; 
          color: ${CONFIG.colors.text};
          font-size: 24px;
          font-weight: 600;
        ">Welcome Back to Club ETO!</h3>
        
        <p style="
          margin: 0 0 20px 0; 
          color: ${CONFIG.colors.text};
          font-size: 16px;
          line-height: 1.5;
        ">
          It looks like you already have an account with us<br>
          <strong>${email}</strong>
        </p>
        
        <div style="margin-top: 25px;">
          <a href="/account/login" style="
            display: inline-block;
            background: ${CONFIG.colors.buttonBg};
            color: ${CONFIG.colors.buttonText};
            padding: 14px 32px;
            text-decoration: none;
            border-radius: 4px;
            margin-right: 15px;
            font-weight: 600;
            transition: opacity 0.2s;
          " onmouseover="this.style.opacity='0.8'" onmouseout="this.style.opacity='1'">
            Sign In to Club ETO
          </a>
          
          <a href="/account/login#recover" style="
            display: inline-block;
            color: ${CONFIG.colors.linkColor};
            padding: 14px 20px;
            text-decoration: none;
            font-weight: 600;
            border-bottom: 2px solid transparent;
            transition: border-color 0.2s;
          " onmouseover="this.style.borderColor='${CONFIG.colors.linkColor}'" onmouseout="this.style.borderColor='transparent'">
            Forgot Your Password?
          </a>
        </div>
        
        <p style="
          margin: 25px 0 0 0;
          font-size: 14px;
          color: #666;
        ">
          Need help? <a href="/pages/contact" style="color: ${CONFIG.colors.linkColor}; text-decoration: underline;">Contact our team</a>
        </p>
      </div>
      
      <style>
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      </style>
    `;
    
    // Hide the form and show the message
    form.style.display = 'none';
    form.insertAdjacentHTML('afterend', messageHTML);
    
    // Scroll to the message
    setTimeout(() => {
      const notice = document.querySelector('.eto-existing-member-notice');
      if (notice) {
        notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  /**
   * Add loading state to form
   */
  function showLoadingState(form) {
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submitButton) {
      submitButton.dataset.originalText = submitButton.textContent || submitButton.value;
      if (submitButton.tagName === 'BUTTON') {
        submitButton.textContent = 'Checking...';
      } else {
        submitButton.value = 'Checking...';
      }
      submitButton.disabled = true;
    }
  }

  /**
   * Remove loading state from form
   */
  function hideLoadingState(form) {
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submitButton && submitButton.dataset.originalText) {
      if (submitButton.tagName === 'BUTTON') {
        submitButton.textContent = submitButton.dataset.originalText;
      } else {
        submitButton.value = submitButton.dataset.originalText;
      }
      submitButton.disabled = false;
    }
  }

  /**
   * Initialize the member detection
   */
  function initMemberDetection() {
    const form = findSmileForm();
    
    if (!form) {
      console.log('Smile.io form not found, will retry...');
      return false;
    }

    console.log('Initializing ETO Smile member detection...');
    
    // Prevent multiple initializations
    if (form.dataset.etoInitialized) {
      return true;
    }
    form.dataset.etoInitialized = 'true';

    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      
      const emailInput = form.querySelector('input[type="email"], input[name*="email"]');
      
      if (!emailInput || !emailInput.value) {
        console.error('Email input not found or empty');
        form.submit(); // Let it proceed normally
        return;
      }

      const email = emailInput.value.trim();
      
      // Show loading state
      showLoadingState(form);
      
      // Check if customer exists
      const exists = await checkIfCustomerExists(email);
      
      if (exists) {
        // Customer exists - show friendly message
        console.log('Existing customer detected:', email);
        hideLoadingState(form);
        showExistingMemberMessage(email, form);
      } else {
        // New customer - proceed with signup
        console.log('New customer, proceeding with signup:', email);
        hideLoadingState(form);
        form.submit();
      }
    });

    return true;
  }

  /**
   * Try to initialize multiple times in case Smile loads slowly
   */
  function retryInit() {
    let attempts = 0;
    const maxAttempts = 5;
    
    const interval = setInterval(() => {
      attempts++;
      
      if (initMemberDetection()) {
        clearInterval(interval);
        console.log('ETO Smile member detection active!');
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        console.log('Could not find Smile form after', maxAttempts, 'attempts');
      }
    }, CONFIG.smileLoadDelay);
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', retryInit);
  } else {
    retryInit();
  }

  // Also watch for dynamic content changes (in case Smile loads via popup)
  const observer = new MutationObserver((mutations) => {
    for (let mutation of mutations) {
      if (mutation.addedNodes.length) {
        initMemberDetection();
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();
